import React from 'react'
import styled from 'styled-components'
import { styleTheme } from '../../../../../../style/theme'
import { getPrimaryColor } from '../../../../../../style/utils'
import { useConfig } from '../../../../../../routes'
import { Price } from '../Price'
import { Button } from '../../../../../../components/ui-elements/Button'
import { BridalPlanProps } from '../../../../BridalPlans.types'
import { Link } from 'react-router-dom'
import { useWindowSize } from 'customer/hooks/useWindowSize'
import { ImageComponent } from '../../../../../../components/ui-elements/Image'

type Props = {
  plan: BridalPlanProps
  bridalFairPageKey: string
  primaryColor: string
}

const MAIN_CONTENT_NAMES = ['挙式', '料理', '新婦衣装', '新郎衣装', '装花', '写真']
const MOBILE_BREAKPOINT = 520

export const Plan: React.FC<Props> = (props) => {
  const { primaryColor } = useConfig() as {
    primaryColor: string
  }
  return <StyledComponent {...props} primaryColor={primaryColor} />
}

const Component: React.FC<
  Props & {
    className?: string
    primaryColor: string
  }
> = ({ className, plan, primaryColor, bridalFairPageKey }) => {
  const contentNames = plan.bridalPlanContents.map((content) => content.name)
  const { width } = useWindowSize()
  const aspectRatio = width <= MOBILE_BREAKPOINT ? 'threeToTwoImage' : 'oneToOneImage'

  return (
    <Link className={className} to={`/customer/public/bridal_fair_pages/${bridalFairPageKey}/plans/${plan.id}`}>
      {plan.mainImageData && (
        <div className="main-image-wrapper">
          <ImageComponent imageData={plan.mainImageData} aspectRatio={aspectRatio} altText="プラントップイメージ" />
        </div>
      )}
      <div className="event-info-wrapper">
        <h4>{plan.name}</h4>
        <div className="event-info-tags">
          {contentNames.includes('挙式') && <span className="event-tag">挙式</span>}
          {contentNames.includes('料理') && <span className="event-tag">料理</span>}
          {contentNames.some((contentName) => contentName.includes('衣装')) && <span className="event-tag">衣装</span>}
          {contentNames.includes('装花') && <span className="event-tag">装花</span>}
          {contentNames.includes('写真') && <span className="event-tag">写真</span>}
          {contentNames.some((name) => !MAIN_CONTENT_NAMES.includes(name)) && <span className="event-tag">その他</span>}
        </div>
        <Price
          displayType={plan.displayType}
          targetNumberOfGuests={plan.targetNumberOfGuests}
          basePrice={plan.basePrice}
          baseDiscount={plan.baseDiscount}
          pricePerAdditionalGuest={plan.pricePerAdditionalGuest}
          discountPerGuest={plan.discountPerGuest}
          primaryColor={primaryColor}
        />
        <div className="event-info-items">
          {plan.minNumberOfGuests && plan.maxNumberOfGuests && (
            <p>{`（適用人数：${plan.minNumberOfGuests}〜${plan.maxNumberOfGuests}名）`}</p>
          )}
        </div>
        <div className="event-info-items">
          <p>{plan.catchphrase}</p>
        </div>
        <div className="event-info-links">
          <Button
            linkTo={`/customer/public/bridal_fair_pages/${bridalFairPageKey}/plans/${plan.id}`}
            color={primaryColor}
            className="show-link"
          >
            詳細を見る
          </Button>
        </div>
      </div>
    </Link>
  )
}

const StyledComponent = styled(Component)`
  display: flex;
  border: 1px solid ${styleTheme.colors.gray.baseLight};
  color: ${styleTheme.colors.black};
  background-color: ${styleTheme.colors.white};
  flex-wrap: wrap;
  font-family: Inter, sans-serif;
  letter-spacing: 0.03rem;
  padding: 24px;
  gap: 24px;
  text-decoration: none;

  @media (max-width: 520px) {
    flex-direction: column;
    padding: 4px;
    gap: 0;
  }

  > .main-image-wrapper {
    @media (max-width: 520px) {
      position: relative;
      width: 100%;
      padding-top: 66.6%;
    }

    > img {
      width: 240px;
      height: 240px;
      object-fit: cover;
      object-position: center;

      @media (max-width: 520px) {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        margin: 0;
      }
    }
  }

  > .event-info-wrapper {
    width: calc(100% - 240px);
    display: flex;
    flex-direction: column;
    gap: 8px 16px;
    flex: 1;

    @media (max-width: 520px) {
      width: 100%;
      height: 100%;
      position: relative;
      padding: 12px;
      margin-left: 0;
      flex: 1;

      > h4 {
        padding-left: 0;
      }
    }

    > h4,
    pre {
      margin: 0;
    }

    > h4 {
      font-size: 16px;
      font-weight: 700;
      @media (max-width: 520px) {
        padding-left: 0;
        margin-top: 0;
        font-size: 16px;
      }
    }

    > pre {
      font-size: 14px;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    > .event-info-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 12px;
      @media (max-width: 520px) {
        margin-left: 0;
      }

      > .event-tag {
        background-color: ${styleTheme.colors.gray.light};
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 13px;
        line-height: 21px;
        font-weight: 400;
        gap: 16px;
      }
    }

    > .event-info-items {
      line-height: 1;
      display: inline-block;
      flex-wrap: wrap-reverse;
      gap: 8px 24px;
      @media (max-width: 520px) {
        width: 100%;
        height: 100%;
        position: relative;
        top: 0;
        left: 4px;
        padding: 0 12px 12px 0;
      }

      > .event-info-minutes {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      > small {
        padding-left: 4px;
      }

      > p {
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        letter-spacing: 0.03rem;
        padding-top: 2px;
      }

      > .event-special-offers {
        display: flex;
        gap: 4px;

        > span {
          color: ${getPrimaryColor};
          font-size: 12px;
          border: 1px solid ${getPrimaryColor};
          padding: 2px 8px;
          border-radius: 2px;
        }
      }
    }

    > .event-info-times {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;

      > span {
        display: flex;
      }
    }

    > .event-info-links {
      display: inline-block;
      gap: 8px;

      > .show-link {
        width: 100px;
        float: right;
        padding: 9px 7px;
        margin: 4px 8px 8px 8px;
        text-decoration: none;
        font-size: 13px;
        line-height: 11px;
      }
    }
  }
`
